import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { apiService } from '../../services/api';

interface ErrorLog {
  id: string;
  level: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  message: string;
  source: string;
  stack_trace: string | null;
  context: Record<string, unknown> | null;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

const ErrorsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);

  const queryClient = useQueryClient();

  const {
    data: errors,
    isLoading,
    error,
  } = useQuery<ErrorLog[]>({
    queryKey: ['tracker-errors', searchTerm, selectedLevel, selectedSource, showResolved],
    queryFn: async (): Promise<ErrorLog[]> => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedLevel) params.append('level', selectedLevel);
      if (selectedSource) params.append('source', selectedSource);
      params.append('resolved', showResolved.toString());

      const response = await apiService.get(`/tracker/troubleshooting/errors?${params.toString()}`);
      return (response as ErrorLog[]) || [];
    },
    refetchInterval: 30000,
  });

  const resolveErrorMutation = useMutation({
    mutationFn: async (errorId: string) => {
      return await apiService.put(`/tracker/troubleshooting/errors/${errorId}/resolve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-errors'] });
    },
  });

  const deleteErrorMutation = useMutation({
    mutationFn: async (errorId: string) => {
      return await apiService.delete(`/tracker/troubleshooting/errors/${errorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-errors'] });
    },
  });

  const handleResolveError = (errorId: string) => {
    resolveErrorMutation.mutate(errorId);
  };

  const handleDeleteError = (errorId: string) => {
    if (window.confirm('Are you sure you want to delete this error log?')) {
      deleteErrorMutation.mutate(errorId);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'text-red-800 bg-red-100';
      case 'ERROR':
        return 'text-red-600 bg-red-50';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-50';
      case 'INFO':
        return 'text-blue-600 bg-blue-50';
      case 'DEBUG':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'CRITICAL':
      case 'ERROR':
        return <XCircleIcon className="h-4 w-4" />;
      case 'WARNING':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'INFO':
        return <InformationCircleIcon className="h-4 w-4" />;
      case 'DEBUG':
        return <ClockIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const filteredErrors = errors || [];
  const sources = Array.from(new Set(filteredErrors.map((error: ErrorLog) => error.source)));
  const levels = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading error logs</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Error Logs</h1>
          <p className="text-gray-600">Monitor and manage application errors</p>
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Show Resolved</span>
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search errors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>
        <div>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Levels</option>
            {levels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            {sources.map((source: string) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-gray-600">Total: {filteredErrors.length} errors</div>
      </div>

      {/* Error List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredErrors.map((error: ErrorLog) => (
            <li key={error.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div
                    className={`flex-shrink-0 ${getLevelColor(error.level)} px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1`}
                  >
                    {getLevelIcon(error.level)}
                    <span>{error.level}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{error.message}</p>
                    <p className="text-sm text-gray-500">
                      Source: {error.source} • {formatTimestamp(error.created_at)}
                    </p>
                    {error.resolved && (
                      <p className="text-xs text-green-600 flex items-center mt-1">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Resolved {error.resolved_at ? formatTimestamp(error.resolved_at) : ''}
                        {error.resolved_by && ` by ${error.resolved_by}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedError(error)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  {!error.resolved && (
                    <button
                      onClick={() => handleResolveError(error.id)}
                      className="text-green-600 hover:text-green-900 p-1"
                      title="Mark as Resolved"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteError(error.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Delete Error"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {filteredErrors.length === 0 && (
          <div className="text-center py-12">
            <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No errors found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedLevel || selectedSource
                ? 'Try adjusting your filters.'
                : 'All errors have been resolved!'}
            </p>
          </div>
        )}
      </div>

      {/* Error Details Modal */}
      {selectedError && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">Error Details</h3>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Level</label>
                    <div
                      className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium mt-1 ${getLevelColor(selectedError.level)}`}
                    >
                      {getLevelIcon(selectedError.level)}
                      <span className="ml-1">{selectedError.level}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Source</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedError.source}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {formatTimestamp(selectedError.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p
                      className={`text-sm mt-1 ${selectedError.resolved ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {selectedError.resolved ? 'Resolved' : 'Unresolved'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded-md">
                    {selectedError.message}
                  </p>
                </div>

                {selectedError.stack_trace && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stack Trace</label>
                    <pre className="text-xs text-gray-900 mt-1 bg-gray-50 p-3 rounded-md overflow-x-auto whitespace-pre-wrap">
                      {selectedError.stack_trace}
                    </pre>
                  </div>
                )}

                {selectedError.context && Object.keys(selectedError.context).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Context</label>
                    <pre className="text-xs text-gray-900 mt-1 bg-gray-50 p-3 rounded-md overflow-x-auto">
                      {JSON.stringify(selectedError.context, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedError.resolved && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolved At</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedError.resolved_at
                          ? formatTimestamp(selectedError.resolved_at)
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolved By</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedError.resolved_by || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                {!selectedError.resolved && (
                  <button
                    onClick={() => {
                      handleResolveError(selectedError.id);
                      setSelectedError(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                  >
                    Mark as Resolved
                  </button>
                )}
                <button
                  onClick={() => setSelectedError(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorsPage;
